// --- React & Redux
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// --- External UI Libraries
import Button from '@dtci/spellbook/Button';
import Typography from '@dtci/spellbook/Typography';
import { Col, Row } from 'antd';
// --- Components
import { PaginationBar } from 'datastage/components/PaginationBar';
import ToastNotificationProvider from 'datastage/components/ToastNotifications';
import { ProductsGrid } from './components/grid';
import { ProductsSearchForm } from './components/search-form';
import { ProductDetailsDrawer } from './components/details-drawer';
import { TwoRowsLayout } from './layout/two-row';
// --- Hooks & Utils
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { getItemRange, getAllItemsRange } from 'datastage/utils/pagination';
import { exportToCsv } from 'datastage/utils/export/exportToCsv';
import { flattenProductsData } from 'datastage/utils/export/flattenProductsData';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { useStoredItemsPerPageSync } from 'datastage/utils/hooks/useStoredItemsPerPageSync';
import { PAGINATION_STORAGE_KEYS } from 'datastage/utils/paginationPreferences';
// --- API & Store
import { useProductsData } from './hooks/useProductsData';
import { useReadyProductsActions } from 'datastage/store/readyProducts/useReadyProducts.js';
import { ReadyProductsModal } from 'datastage/pages/Products/components/modal/index.jsx';
import { usePermissions } from 'datastage/utils/permissions';

/**
 * Products Page
 *
 * Features:
 * - Displays a paginated list of products
 * - Provides search, export, and refresh functionality
 * - Handles loading and toast notifications
 * - Uses a two-row layout for search and grid
 * - Implements refreshProducts flag pattern for clean separation of concerns
 * - Persists page size preference to localStorage automatically
 *
 * @component
 * @returns {JSX.Element} The rendered products management page.
 */
export const ProductsPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showErrorToast } = useToasts();
    const { fetchProducts, updateProducts, productRange, itemsPerPage } = useProductsData();
    const { updateReadyProducts } = useReadyProductsActions();
    const { isRLProductGateUser } = usePermissions();
    const { totalPages, currentPage, totalItems, refreshProducts, pageSizeOptions } = useSelector(
        (state) => state.products,
    );

    /**
     * Sync persisted page size preference from localStorage on mount.
     * If stored preference differs from Redux state, automatically updates Redux.
     */
    useStoredItemsPerPageSync({
        storageKey: PAGINATION_STORAGE_KEYS.products,
        itemsPerPage,
        rangeKey: 'productRange',
        onSync: updateProducts,
    });

    /**
     * Listen for refreshProducts flag and refresh the grid.
     * This pattern allows other components (like drawers) to trigger refreshes
     * without duplicating fetch logic.
     */
    useEffect(() => {
        if (refreshProducts) {
            handleRefresh();
            updateProducts({ refreshProducts: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshProducts]);

    /**
     * Handle page change for pagination (←/→ buttons).
     * Fetches directly without updating URL (TAP-UI pattern).
     * Page size changes go through URL → search form handles those.
     */
    const handlePageChange = async (pageNumber) => {
        if (pageNumber === currentPage) return;

        const nextPageRange = getItemRange(pageNumber, itemsPerPage);
        const data = await fetchProducts(nextPageRange);

        if (data) {
            updateProducts({
                products: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                totalItems: data?.totalElements ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                productRange: nextPageRange,
            });
        }
    };

    /**
     * Handle page size change from dropdown.
     * Updates URL with new page size - the search form will handle the fetch.
     * This prevents duplicate fetches (TAP-UI pattern).
     */
    const handlePageSizeChange = (newSize) => {
        if (newSize === itemsPerPage) return;

        // Update URL with new page size
        // Search form will see URL change and fetch with new size
        const currentParams = parseSearchParams(location.search);
        updateSearchParams(location, navigate, {
            ...currentParams,
            pageSize: newSize,
        });
    };

    /**
     * Refresh the products list from the API.
     */
    const handleRefresh = async () => {
        const data = await fetchProducts(productRange);

        if (data) {
            updateProducts({
                products: data?.content ?? [],
                totalItems: data?.totalElements ?? 0,
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
            });
        }
    };

    /**
     * Export all products to CSV.
     * Fetches all records using the total count from the current result set.
     */
    const handleExport = async () => {
        const exportRange = getAllItemsRange(totalItems);
        const data = await fetchProducts(exportRange);

        if (data?.code) {
            showErrorToast(data?.message || 'Failed to export products.');
            return;
        }

        // Expand nested product data for CSV export
        const expandedRows = (data?.content || []).flatMap((product) =>
            flattenProductsData(product),
        );

        exportToCsv(expandedRows, 'products');
    };

    const onOpenReadyProductsModal = () => {
        updateReadyProducts({
            isModalOpen: true,
        })
    }

    return (
        <>
            <Row
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 20,
                }}
            >
                <Col>
                    <Typography variant="T50" style={{ marginLeft: 20 }}>
                        Products
                    </Typography>
                </Col>
                <Col style={{ display: 'flex', gap: 8 }}>
                    <Button
                        labelText="Push Ready"
                        styleType="link"
                        onClick={onOpenReadyProductsModal}
                    >
                        Push Ready
                    </Button>
                    <Button
                        iconType="fa-solid fa-arrow-down-to-line"
                        labelText="Export"
                        styleType="link"
                        onClick={handleExport}
                        disabled={totalItems === 0}
                    >
                        Export
                    </Button>
                    <Button
                        iconType="fa-solid fa-rotate"
                        labelText="Refresh"
                        styleType="link"
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>

            <TwoRowsLayout>
                <main data-position="top">
                    <ProductsSearchForm />
                </main>
                <main data-position="bottom">
                    <ProductsGrid />
                </main>
            </TwoRowsLayout>

            <PaginationBar
                totalPages={totalPages}
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={pageSizeOptions}
            />

            {/* Product details drawer for viewing product information */}
            <ProductDetailsDrawer />
            <ReadyProductsModal />
            <ToastNotificationProvider />
        </>
    );
};
